import { scrollPage, waitForPage, hashTarget, scrollToElement } from '@/runtime/smooth-scroll'
import { releaseViewTransition } from '@/runtime/view-transition'
import { preloadRouteContent } from '@/runtime/content-preload'
import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import { handOffUnknownRoute } from './unknown-route.mjs'

import manifest from '@/data/route-manifest.json'
import projectAliases from '@/data/project-aliases.json'
// Route names, metadata and public URLs all derive from the build manifest.
const components = {
  pageIndex: () => import('@/components/pages/pageIndex.vue'),
  pageWorks: () => import('@/components/pages/pageWorks.vue'),
  pageWorksShowcase: () => import('@/components/pages/pageWorksShowcase.vue'),
  pageAbout: () => import('@/components/pages/pageAbout.vue'),
  pageQuotes: () => import('@/components/pages/pageQuotes.vue'),
  pageLifetree: () => import('@/components/pages/pageLifetree.vue'),
  pageCV: () => import('@/components/pages/pageCV.vue'),
  pageMuse: () => import('@/components/pages/pageMuse.vue'),
  pageExperiment: () => import('@/components/pages/pageExperiment.vue'),
  pageResearch: () => import('@/components/pages/pageResearch.vue'),
  pageThesis: () => import('@/components/pages/pageThesis.vue'),
  pageLab: () => import('@/components/pages/pageLab.vue'),
  pageLabHome: () => import('@/components/pages/pageLabHome.vue'),
  pageLineage: () => import('@/components/pages/pageLineage.vue'),
  pageMusic: () => import('@/components/pages/pageMusic.vue'),
  pageMusicTrack: () => import('@/components/pages/pageMusicTrack.vue'),
  pageNft: () => import('@/components/pages/pageNft.vue'),
  pageLexus2023: () => import('@/components/pages/pageLexus2023.vue'),
  pageWritingDetail: () => import('@/components/pages/pageWritingDetail.vue'),
  pageDialogues: () => import('@/components/pages/pageDialogues.vue'),
  pageThinking: () => import('@/components/pages/pageThinking.vue'),
  pageTeaching: () => import('@/components/pages/pageTeaching.vue'),
  pageDialogueDetail: () => import('@/components/pages/pageDialogueDetail.vue'),
  pagePress: () => import('@/components/pages/pagePress.vue'),
  pageSearch: () => import('@/components/pages/pageSearch.vue'),
  pageSitemap: () => import('@/components/pages/pageSitemap.vue'),
  pageContact: () => import('@/components/pages/pageContact.vue'),
  pageWorkDetail: () => import('@/components/pages/pageWorkDetail.vue'),
  pageSketchDetail: () => import('@/components/pages/pageSketchDetail.vue'),
  pageTdDetail: () => import('@/components/pages/pageTdDetail.vue'),
  pageTdIndex: () => import('@/components/pages/pageTdIndex.vue'),
  pageExhibitionDetail: () => import('@/components/pages/pageExhibitionDetail.vue'),
  pageLegacyProject: () => import('@/components/pages/pageLegacyProject.vue'),
  pageNotFound: () => import('@/components/pages/pageNotFound.vue'),
}

const routes = manifest.map(entry => ({
  // Literal enums retain route.params for existing detail components while preventing unknown slugs.
  path: Object.entries(entry.params || {}).reduce((p, [key, value]) =>
    p.replace('/' + value + '/', '/:' + key + '(' + value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')/'), entry.path),
  name: entry.name || entry.path,
  component: components[entry.component],
  meta: { ...entry.meta, publicEntry: entry },
}))
const aliases = { ...projectAliases, '/arts': '/work/', '/zh/arts': '/zh/work/', '/writing': '/thinking/', '/zh/writing': '/zh/thinking/' }
for (const [path, redirect] of Object.entries(aliases)) routes.push({ path, redirect })
routes.push(
  { path: '/en', redirect: '/' },
  { path: '/en/:pathMatch(.*)*', redirect: to => '/' + [].concat(to.params.pathMatch || []).join('/') },
  // 前端不認得的路交回伺服器（舊網址 301、/archive/ 靜態頁都在那一層）；只有伺服器給的首屏才顯示找不到 → unknown-route.mjs
  { path: '/:pathMatch(.*)*', name: 'not-found', component: components.pageNotFound, meta: { title: 'Page not found', noindex: true },
    beforeEnter: (to, from) => handOffUnknownRoute(to, from, { start: START_LOCATION }) },
)
// 返回時還原位置：頁面高度不夠（圖片或分批內容還沒長出來）就在之後幾幀補捲
function restoreScroll({ top = 0, left = 0 }) {
  scrollPage(top, left)
  let tries = 0
  const retry = () => {
    if (Math.abs(window.scrollY - top) < 2 || ++tries > 20) return
    if (document.documentElement.scrollHeight - window.innerHeight >= top - 2) { scrollPage(top, left); return }
    requestAnimationFrame(retry)
  }
  if (Math.abs(window.scrollY - top) >= 2) requestAnimationFrame(retry)
}
const router = createRouter({
  history: createWebHistory(), routes,
  // 捲動定位在 View Transition 的 update 裡完成（新畫面拍照之前），做完才放行轉場。
  async scrollBehavior(to, from, savedPosition) {
    if (!savedPosition && to.path === from.path && to.hash === from.hash) return false
    if (to.path !== from.path) await waitForPage(to.path)
    if (savedPosition) { restoreScroll(savedPosition); releaseViewTransition(); return false }
    if (to.hash) {
      const samePage = to.path === from.path
      const target = hashTarget(to.hash)
      // 同頁錨點滑行 560ms；跨頁帶錨點在轉場中直接定位
      if (target) scrollToElement(target, { smooth: samePage })
      else if (!samePage) {
        scrollPage(0)
        setTimeout(() => { const late = hashTarget(to.hash); if (late) scrollToElement(late, { smooth: true }) }, 400)
      }
      releaseViewTransition()
      return false
    }
    if (to.path === from.path) return false
    scrollPage(0)
    releaseViewTransition()
    return false
  },
})
// 文章／對話錄內文在導航確認前就載好（頁面掛載當下就有內文，不再先閃「Loading…」與頁尾）
router.beforeResolve(to => preloadRouteContent(to))

export default router
